/**
 * QUI-217 / QUI-220: capture static fallback page HTML as Markdown so the
 * CMS editor can pre-fill an empty draft (from public EDIT or admin list).
 */

import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import {
	captureContentBlocksFromDom,
	expandCmsCollapsiblesForSeed,
	storeContentBlocksSeed,
} from '$lib/content/sitePageContentBlocks';

const STORAGE_PREFIX = 'quilty.siteCmsStaticSeed:';

/** Canonical CMS-capable paths (see backend DEFAULT_SITE_PAGES). */
export const SITE_CMS_PATHS = [
	'/privacy',
	'/terms',
	'/accessibility',
	'/why-quilty',
	'/help',
	'/enterprise',
] as const;

export function normalizeSiteCmsPath(path: string): string {
	const trimmed = path.trim();
	if (!trimmed) return '/';
	return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function siteCmsSeedStorageKey(path: string): string {
	return `${STORAGE_PREFIX}${normalizeSiteCmsPath(path)}`;
}

export function storeSiteCmsStaticSeed(path: string, markdown: string): void {
	if (typeof sessionStorage === 'undefined') return;
	const trimmed = markdown.trim();
	if (!trimmed) return;
	sessionStorage.setItem(siteCmsSeedStorageKey(path), trimmed);
}

export function consumeSiteCmsStaticSeed(path: string): string | null {
	if (typeof sessionStorage === 'undefined') return null;
	const key = siteCmsSeedStorageKey(path);
	const value = sessionStorage.getItem(key);
	if (value == null) return null;
	sessionStorage.removeItem(key);
	return value.trim() || null;
}

function buildTurndown(): TurndownService {
	const service = new TurndownService({
		headingStyle: 'atx',
		codeBlockStyle: 'fenced',
		bulletListMarker: '-',
		emDelimiter: '*',
		strongDelimiter: '**',
		linkStyle: 'inlined',
	});
	service.use(gfm);
	service.remove((node) => {
		const name = node.nodeName.toLowerCase();
		return (
			name === 'script' ||
			name === 'style' ||
			name === 'noscript' ||
			name === 'svg' ||
			name === 'button' ||
			name === 'nav' ||
			name === 'footer'
		);
	});
	service.addRule('stripButtonsAndInputs', {
		filter: ['button', 'input', 'textarea', 'select'],
		replacement: () => '',
	});
	return service;
}

/** Prefer article prose; fall back to main landmark / seed root. */
export function pickStaticSeedRoot(doc: ParentNode = document): Element | null {
	if (doc.querySelector('[data-cms-sourced="1"]')) {
		return null; // already rendering a published CMS body
	}
	return (
		doc.querySelector('[data-cms-seed-root]') ||
		doc.querySelector('main#main-content article') ||
		doc.querySelector('main#main-content') ||
		doc.querySelector('main article') ||
		doc.querySelector('#main-content') ||
		doc.querySelector('main')
	);
}

export function htmlElementToMarkdown(root: Element): string {
	const clone = root.cloneNode(true) as Element;
	// QUI-228: expand collapsed FAQ / <details> before stripping chrome.
	expandCmsCollapsiblesForSeed(clone);
	// Replace FAQ toggle buttons with plain question text so Turndown keeps them.
	const doc = clone.ownerDocument ?? document;
	clone.querySelectorAll('[data-cms-faq-item]').forEach((item) => {
		const q = item.querySelector('[data-cms-faq-q]');
		const btn = item.querySelector('button');
		if (q && btn) {
			const heading = doc.createElement('h3');
			heading.textContent = q.textContent || '';
			btn.replaceWith(heading);
		}
	});
	clone
		.querySelectorAll(
			'[data-testid="cms-edit-button"], [data-cms-block-edit], script, style, noscript, svg, button'
		)
		.forEach((el) => el.remove());
	// QUI-233: structured FAQ/sections live in meta.content_blocks — keep them
	// out of the body Markdown seed so Inline/SAVE does not duplicate them.
	clone.querySelectorAll('[data-cms-block-type]').forEach((el) => el.remove());
	const html = clone.innerHTML.trim();
	if (!html) return '';
	const md = buildTurndown().turndown(html);
	return md.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Capture the visible static fallback as Markdown and stash it for the
 * admin editor (`?seed=1`). Returns false when there is nothing to seed
 * (CMS body already showing, or empty DOM).
 */
export function captureStaticFallbackSeed(path: string): boolean {
	const root = pickStaticSeedRoot();
	if (!root) return false;
	const blocks = captureContentBlocksFromDom(root);
	if (blocks.length) storeContentBlocksSeed(path, blocks);
	const markdown = htmlElementToMarkdown(root);
	if (!markdown && !blocks.length) return false;
	if (markdown) storeSiteCmsStaticSeed(path, markdown);
	return true;
}

/**
 * QUI-220: when editing from /admin/site-pages (no live DOM / session seed),
 * fetch the public route HTML and convert the static fallback to Markdown.
 * Returns null when the route is CMS-sourced, empty, or unreachable.
 */
export async function fetchStaticFallbackMarkdown(
	path: string
): Promise<string | null> {
	if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
		return null;
	}
	const normalized = normalizeSiteCmsPath(path);
	try {
		const res = await fetch(normalized, {
			credentials: 'same-origin',
			headers: { Accept: 'text/html' },
		});
		if (!res.ok) return null;
		const html = await res.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const root = pickStaticSeedRoot(doc);
		if (!root) return null;
		const markdown = htmlElementToMarkdown(root);
		return markdown.trim() || null;
	} catch {
		return null;
	}
}

/** Pull a non-empty body from a revision snapshot (after, then before). */
export function bodyMarkdownFromRevisionSnapshot(
	snapshot: Record<string, unknown> | null | undefined
): string | null {
	if (!snapshot || typeof snapshot !== 'object') return null;
	const raw = snapshot.body_markdown;
	if (typeof raw !== 'string') return null;
	const trimmed = raw.trim();
	return trimmed || null;
}
